import type { ReactElement, ReactNode } from 'react';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { cn } from '@/lib/cn';

export type ArrangementLineProps = {
  name: string;
  musicalKey?: string;
  form?: string;
  arranger?: string;
  inRepertoire?: boolean;
  expanded: boolean;
  onToggle: () => void;
  onAdd: () => void;
  children?: ReactNode;
};

export function ArrangementLine({
  name, musicalKey, form, arranger, inRepertoire = false,
  expanded, onToggle, onAdd, children,
}: ArrangementLineProps): ReactElement {
  return (
    <div
      className={cn(
        'border rounded-[var(--r-md)] bg-[var(--bg-surface)] overflow-hidden transition-colors',
        expanded ? 'border-[var(--ink)]' : 'border-[var(--rule)]',
      )}
    >
      <div className="w-full flex items-center gap-4 p-3 hover:bg-[var(--bg-soft)]/50 transition-colors">
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 text-left"
          aria-expanded={expanded}
        >
          <div className="font-display text-[15px] font-semibold text-[var(--ink)]">{name}</div>
          <div className="flex items-center gap-1.5 text-[11px] text-[var(--ink-3)] mt-0.5 flex-wrap">
            {musicalKey && <span className="font-mono">{musicalKey}</span>}
            {musicalKey && (form || arranger) && <span>·</span>}
            {form && <span>{form}</span>}
            {form && arranger && <span>·</span>}
            {arranger && <span className="truncate">arr. {arranger}</span>}
          </div>
        </button>
        {inRepertoire ? (
          <Badge tone="ok" size="sm" icon="check">In repertoire</Badge>
        ) : (
          <Button size="sm" variant="primary" icon="plus" onClick={onAdd}>
            Add
          </Button>
        )}
      </div>
      {expanded && children && (
        <div className="px-3 pb-3 border-t border-[var(--rule)] pt-3">{children}</div>
      )}
    </div>
  );
}
